import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, FolderKanban, ClipboardList } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import {
  Card, Button, Field, Input, Select, Textarea,
} from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { SitePicker } from "@/features/sites/SitePicker";

// =============================================================================
// New project / new BID
// =============================================================================
// FIXED 21 Apr 2026 — column names now match the real Phase 1 schema
// (bids.bid_title, bids.due_date, bids.client_ref, projects.notes, etc).
// See schema_dump.csv for the source of truth.
// =============================================================================

/** A pasted scope as plain lines: bullets and numbering become "- ", NBSP / tabs become spaces, blank runs collapse. */
function tidyScope(t: string): string {
  return t.replace(/\r\n?/g, "\n").replace(/[\u00a0\u2007\u202f\t]+/g, " ")
    .split("\n").map(l => l.replace(/^\s*(?:[•·▪◦‣∙●■\-–—*]+|\d+[.)])\s+/, "- ").replace(/\s+$/, "").replace(/^\s+/, m => m.length >= 2 ? "  " : ""))
    .join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

export default function NewProjectScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"bid" | "project">("project");

  // Shared fields
  const [client_id, setClientId] = useState<string>("");
  const [carrier_id, setCarrierId] = useState<string>("");
  const [site_id, setSiteId] = useState<string>("");
  const [project_name, setProjectName] = useState("");
  const [scope_summary, setScope] = useState("");
  const [contract_value, setContractValue] = useState(""); // used as contract_value for projects, estimated_value for bids
  const [scheduled_start_date, setStartDate] = useState("");
  const [scheduled_end_date, setEndDate] = useState("");
  const [is_high_risk_construction_work, setHrcw] = useState(false);

  // BID-only
  const [bid_reference, setBidRef] = useState("");
  const [bid_due_date, setBidDueDate] = useState("");

  const { data: clients = [] }  = useQuery({ queryKey: ["clients"],  queryFn: async () => (await supabase.from("clients").select("client_id, client_code, legal_name").eq("is_active", true).order("legal_name")).data ?? [] });
  const { data: carriers = [] } = useQuery({ queryKey: ["carriers"], queryFn: async () => (await supabase.from("carriers").select("carrier_id, carrier_code, legal_name").eq("is_active", true).order("carrier_code")).data ?? [] });
  // sites are sourced from SitePicker (handles its own query + inline create)

  // ---------------------------------------------------------------------------
  // Create PROJECT
  // ---------------------------------------------------------------------------
  const createProjectMut = useMutation({
    mutationFn: async () => {
      if (!client_id || !project_name || !site_id) throw new Error("missing_fields_project");
      const { data, error } = await supabase
        .from("projects")
        .insert({
          client_id,
          carrier_id: carrier_id || null,
          site_id,                                        // NOT NULL on projects
          project_name,
          // T-162a — project_code is auto-generated by `app.generate_project_code()`
          // BEFORE INSERT trigger (mig 006). Empty string triggers regeneration; the
          // trigger overwrites it with `${client_code}-${YY}-${NNNN}`.
          project_code: "",
          notes: scope_summary || null,                   // FIXED: projects has `notes`, not `scope_summary`
          contract_value: contract_value ? parseFloat(contract_value) : null,
          scheduled_start_date: scheduled_start_date || null,
          scheduled_end_date: scheduled_end_date || null,
          is_high_risk_construction_work,
          project_manager_id: user?.employee_id ?? null,
          status: "scheduled",                            // 1 Sep 2026 - James: a new job starts SCHEDULED (mig 341)
        })
        .select("project_id, project_code")
        .single();
      if (error) throw error;

      // Queue folder skeleton creation via the outbox (unchanged)
      await supabase.from("notifications").insert({
        channel: "in_app",
        event_type: "project.activated",
        recipient_hint: "worker:sharepoint",
        payload: { project_id: data.project_id, project_code: data.project_code },
        related_table: "projects",
        related_id: data.project_id,
      });

      return data.project_id;
    },
    onSuccess: (id) => navigate(`/projects/${id}`),
  });

  // ---------------------------------------------------------------------------
  // Create BID
  // ---------------------------------------------------------------------------
  const createBidMut = useMutation({
    mutationFn: async () => {
      if (!client_id || !project_name) throw new Error("missing_fields_bid");
      const { data, error } = await supabase
        .from("bids")
        .insert({
          client_id,
          carrier_id: carrier_id || null,
          site_id: site_id || null,                       // bids allow null site (tender stage)
          bid_title: project_name,
          scope_summary: scope_summary || null,
          client_ref: bid_reference || null,              // FIXED: was `bid_reference`
          due_date: bid_due_date || null,                 // FIXED: was `bid_due_date`
          estimated_value: contract_value ? parseFloat(contract_value) : null,
          // status defaults to 'draft' in the DB — omit to let default fire
          // REMOVED: is_high_risk_construction_work (not a column on bids)
          // REMOVED: assigned_to_employee_id (not a column on bids)
        })
        .select("bid_id, bid_number")
        .single();
      if (error) throw error;
      return data.bid_id;
    },
    onSuccess: (id) => navigate(`/bids/${id}`),
  });

  // Error messages
  const activeMut = mode === "bid" ? createBidMut : createProjectMut;
  const errMsg = activeMut.error
    ? (() => {
        const msg = (activeMut.error as any)?.message;
        if (msg === "missing_fields_bid")     return "Client and BID title are required.";
        if (msg === "missing_fields_project") return "Client, project name, and site are required.";
        return msg ?? "Something went wrong. Check DevTools → Console for details.";
      })()
    : null;

  const canSubmit = mode === "bid"
    ? !!client_id && !!project_name.trim()
    : !!client_id && !!project_name.trim() && !!site_id;

  const submitting = createProjectMut.isPending || createBidMut.isPending;

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6 pb-40">
      <button onClick={() => navigate(-1)} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> Back
      </button>

      <h1 className="text-2xl font-semibold tracking-tight mb-1">New {mode === "bid" ? "BID" : "project"}</h1>
      <div className="text-text-muted text-[13px] mb-5">
        {mode === "bid"
          ? "Slim record for tender / RFQ stage. Gets a lightweight folder. Promote to a full project when you win the work."
          : "Full project record. Creates the SharePoint folder skeleton automatically."}
      </div>

      {/* Mode toggle */}
      <div className="grid grid-cols-2 gap-2 mb-5">
        <button
          onClick={() => setMode("project")}
          className={`flex items-center gap-3 px-4 py-3 rounded border-2 text-left transition-colors ${
            mode === "project" ? "border-accent bg-accent/10" : "border-line bg-ink-raised hover:border-line-light"
          }`}
        >
          <FolderKanban size={18} className={mode === "project" ? "text-accent" : "text-text-muted"} />
          <div>
            <div className={`text-[13px] font-medium ${mode === "project" ? "text-accent" : "text-text"}`}>Project</div>
            <div className="text-[11px] text-text-muted">Won work — full folder set</div>
          </div>
        </button>
        <button
          onClick={() => setMode("bid")}
          className={`flex items-center gap-3 px-4 py-3 rounded border-2 text-left transition-colors ${
            mode === "bid" ? "border-accent bg-accent/10" : "border-line bg-ink-raised hover:border-line-light"
          }`}
        >
          <ClipboardList size={18} className={mode === "bid" ? "text-accent" : "text-text-muted"} />
          <div>
            <div className={`text-[13px] font-medium ${mode === "bid" ? "text-accent" : "text-text"}`}>BID / RFQ</div>
            <div className="text-[11px] text-text-muted">Tender — slim folder, promote on win</div>
          </div>
        </button>
      </div>

      <Card>
        <div className="p-5 space-y-4">
          <Field label="Client *">
            <Select value={client_id} onChange={e => setClientId(e.target.value)}>
              <option value="">— select client —</option>
              {(clients as any[]).map(c => <option key={c.client_id} value={c.client_id}>{c.client_code} · {c.legal_name}</option>)}
            </Select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Carrier">
              <Select value={carrier_id} onChange={e => setCarrierId(e.target.value)}>
                <option value="">— none —</option>
                {(carriers as any[]).map(c => <option key={c.carrier_id} value={c.carrier_id}>{c.carrier_code}</option>)}
              </Select>
            </Field>
            <Field label={mode === "project" ? "Site *" : "Site"}>
              <SitePicker
                value={site_id}
                onChange={setSiteId}
                placeholder={mode === "project" ? "— select site —" : "— TBD —"}
              />
            </Field>
          </div>

          <Field label={mode === "bid" ? "BID title *" : "Project name *"}>
            <Input value={project_name} onChange={e => setProjectName(e.target.value)} placeholder="e.g. NBN upgrade — Toowoomba rooftop" />
          </Field>

          <Field label={mode === "bid" ? "Scope summary" : "Scope / notes"}>
            {/* 3 Nov 2026 — James pastes scope from Claude Projects: "this scope format is not letting me, can we clean that up".
                The paste is tidied into plain lines (bullets → "- ", odd spaces and tabs out), and the box grows with it. */}
            <Textarea rows={Math.min(24, Math.max(3, scope_summary.split("\n").length + 1))} value={scope_summary}
              onChange={e => setScope(e.target.value)}
              onPaste={e => { const t = e.clipboardData.getData("text/plain"); if (!t) return; e.preventDefault(); const tidy = tidyScope(t); const el = e.currentTarget; const before = scope_summary.slice(0, el.selectionStart ?? scope_summary.length); const after = scope_summary.slice(el.selectionEnd ?? scope_summary.length); setScope(`${before}${tidy}${after}`); }}
              placeholder="Bullet-style high-level scope is fine here — paste from anywhere, it is tidied into plain lines." />
          </Field>

          {mode === "project" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contract value">
                  <Input type="number" mono autoComplete="off" value={contract_value} onChange={e => setContractValue(e.target.value)} placeholder="ex-GST" />
                </Field>
                <Field label="Start date">
                  <Input type="date" mono autoComplete="off" value={scheduled_start_date} onChange={e => setStartDate(e.target.value)} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="End date">
                  <Input type="date" mono autoComplete="off" value={scheduled_end_date} onChange={e => setEndDate(e.target.value)} />
                </Field>
                <div />
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Estimated value">
                  <Input type="number" mono autoComplete="off" value={contract_value} onChange={e => setContractValue(e.target.value)} placeholder="ex-GST" />
                </Field>
                <Field label="BID due">
                  <Input type="date" mono autoComplete="off" value={bid_due_date} onChange={e => setBidDueDate(e.target.value)} />
                </Field>
              </div>
              <Field label="BID reference (client's RFQ number)">
                <Input mono autoComplete="off" value={bid_reference} onChange={e => setBidRef(e.target.value)} placeholder="e.g. RFQ-2026-4821" />
              </Field>
            </>
          )}

          {mode === "project" && (
            <label className="flex items-center gap-3 cursor-pointer pt-2">
              <input type="checkbox" checked={is_high_risk_construction_work} onChange={e => setHrcw(e.target.checked)} className="accent-accent" />
              <div>
                <div className="text-[13px] text-text">High-risk construction work</div>
                <div className="text-[11px] text-text-muted">Ticking this triggers mandatory SWMS.</div>
              </div>
            </label>
          )}
        </div>
      </Card>

      <StickyBottomBar>
        {errMsg && (
          <div className="px-3 py-2 bg-red-900/30 border-b border-red-900/50 text-red-200 text-[12px] text-center">
            ⚠️ {errMsg}
          </div>
        )}
        <div className="p-3 flex justify-center">
          <div className="flex gap-2 max-w-2xl w-full">
            <Button variant="secondary" size="lg" onClick={() => navigate(-1)}>Cancel</Button>
            <Button
              size="lg"
              icon={CheckCircle2}
              onClick={() => mode === "bid" ? createBidMut.mutate() : createProjectMut.mutate()}
              disabled={!canSubmit || submitting}
              className="flex-1 justify-center"
            >
              {submitting ? "Creating…" : mode === "bid" ? "Create BID" : "Create project"}
            </Button>
          </div>
        </div>
      </StickyBottomBar>
    </div>
  );
}
